'use client';

import React, { useState, useEffect } from 'react';
import { IoMdHome } from 'react-icons/io';
import { HiMenu, HiX } from 'react-icons/hi';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations, useLocale } from 'next-intl';

import eramHeaderLogo from '@/assets/imgs/eram-header-logo.webp';
import { useActiveSection } from '@/app/hooks/useActiveSection';
import { usePermissions } from '@/contexts/PermissionsContext';
import { studioClient } from '@/sanity/lib/client';
import { navigationLabelsQuery } from '@/sanity/queries/navigationLabels';
import { NavigationLabels } from '@/types/navigationLabels';

import { LanguageSwitcher } from '@/shared';

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navigationLabels, setNavigationLabels] = useState<NavigationLabels | null>(null);
  const [, setLoading] = useState(true);
  const activeSection = useActiveSection();
  const t = useTranslations('navigation');
  const locale = useLocale();
  const { isSectionVisibleInHeader } = usePermissions();

  useEffect(() => {
    const fetchNavigationLabels = async () => {
      try {
        const data = await studioClient.fetch<NavigationLabels>(navigationLabelsQuery);
        setNavigationLabels(data);
      } catch (error) {
        console.error('Error fetching navigation labels:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNavigationLabels();
  }, []);

  // Helper function to get label with fallback
  const getLabel = (key: keyof NavigationLabels, fallbackKey: string) => {
    if (navigationLabels && navigationLabels[key]) {
      return navigationLabels[key][locale as 'en' | 'ar'] || navigationLabels[key].en;
    }
    return t(fallbackKey);
  };

  const allNavItems = [
    {
      href: '#home',
      sectionId: 'home',
      icon: (
        <div aria-hidden="true">
          <IoMdHome size={20} className="text-white" />
        </div>
      ),
      permissionKey: 'home' as const,
    },
    {
      label: getLabel('companies', 'companies'),
      href: '#companies',
      sectionId: 'companies',
      permissionKey: 'companies' as const,
    },
    {
      label: getLabel('aboutUs', 'aboutUs'),
      href: '#about-us',
      sectionId: 'about-us',
      permissionKey: 'aboutUs' as const,
    },
    {
      label: getLabel('ourValues', 'ourValues'),
      href: '#our-values',
      sectionId: 'our-values',
      permissionKey: 'ourValues' as const,
    },
    {
      label: getLabel('objective', 'objective'),
      href: '#objective',
      sectionId: 'objective',
      permissionKey: 'objective' as const,
    },
    {
      label: getLabel('motion', 'motion'),
      href: '#motion',
      sectionId: 'motion',
      permissionKey: 'motion' as const,
    },
    {
      label: getLabel('news', 'news'),
      href: '#news',
      sectionId: 'news',
      permissionKey: 'news' as const,
    },
    {
      label: getLabel('ourClients', 'ourClients'),
      href: '#our-clients',
      sectionId: 'our-clients',
      permissionKey: 'ourClients' as const,
    },
    {
      label: getLabel('contactUs', 'contactUs'),
      href: '#contact-us',
      sectionId: 'contact-us',
      permissionKey: 'contactUs' as const,
    },
  ];

  // Filter nav items based on permissions
  const navItems = allNavItems.filter((item) => isSectionVisibleInHeader(item.permissionKey));

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (e: any, href: string) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      const headerHeight = 80; // Approximate header height
      const elementPosition = targetElement.offsetTop - headerHeight;

      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      });
    }

    closeMobileMenu();
  };

  return (
    <header className="fixed top-0 right-0 left-0 z-50 p-4 backdrop-blur-xl lg:p-5" role="banner">
      <div className="max-w-laptop mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="h-[2rem] w-[8rem] lg:h-[2.5rem] lg:w-[10rem]">
          <Image
            src={eramHeaderLogo}
            alt="Eram Group Logo"
            className="h-full w-full object-contain"
            priority={true}
            sizes="(max-width: 1024px) 128px, 160px"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:block">
          <ul className="flex items-center gap-5" role="list">
            {navItems.map((item, idx) => {
              const isActive = activeSection === item.sectionId;
              return (
                <li key={idx} role="listitem">
                  <Link
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={`flex items-center rounded-full p-3 text-sm font-bold transition-colors ${
                      isActive
                        ? 'border-[0.75px] border-[#3FB988] bg-gradient-to-b from-transparent to-[rgba(63,185,136,0.60)] text-white backdrop-blur-[1.5px]'
                        : 'font-bold text-gray-300 hover:text-gray-300'
                    }`}>
                    {item.icon && item.icon}
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Desktop Language Switcher */}
        <div className="hidden lg:block">
          <LanguageSwitcher />
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <button
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            type="button"
            onClick={toggleMobileMenu}
            className="relative z-50 cursor-pointer text-white transition-colors duration-200 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none">
            {isMobileMenuOpen ? <HiX size={24} /> : <HiMenu size={24} />}
          </button>
        </div>

        {/* Mobile Sidebar */}
        <div
          id="mobile-menu"
          className={`fixed inset-0 z-40 transition-all duration-300 ease-in-out lg:hidden`}
          aria-hidden={!isMobileMenuOpen}>
          {/* Backdrop */}

          {/* Sidebar */}
          <div
            className={`absolute top-16 right-0 h-dvh w-full bg-black backdrop-blur-xl transition-transform duration-300 ease-in-out ${
              isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation menu">
            {/* Mobile Navigation */}
            <nav className="p-6">
              <ul className="space-y-4" role="list">
                {navItems.map((item, idx) => {
                  const isActive = activeSection === item.sectionId;
                  return (
                    <li
                      key={idx}
                      role="listitem"
                      className={`w-fit transform transition-all duration-300 ease-in-out ${
                        isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                      }`}
                      style={{ transitionDelay: `${idx * 100}ms` }}>
                      <Link
                        href={item.href}
                        onClick={(e) => handleNavClick(e, item.href)}
                        className={`flex items-center gap-2 rounded-lg px-4 py-3 text-lg font-medium transition-all duration-200 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none ${
                          isActive
                            ? 'border-l-4 border-[#3FE5A2] bg-[#3FE5A2]/10 text-[#3FE5A2]'
                            : 'text-white hover:text-gray-300'
                        }`}>
                        {item.icon && item.icon}
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Mobile Language Switcher */}
              <div
                className={`mt-auto transform transition-all duration-300 ease-in-out ${
                  isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
                style={{ transitionDelay: '500ms' }}>
                <LanguageSwitcher />
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
}
